import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SetupPortalPage from '../../components/setup/SetupPortalPage';
import BackToAgentSetupLink from '../../components/setup/BackToAgentSetupLink';
import { AGENT_SETUP_BASE } from '../../constants/setupRoadmapSteps';
import api from '../../API.js';
import { monitorRagJobStatus } from '../../utils/ragUploadHelper';
import { useGirderUpdates } from '../../contexts/GirderUpdatesContext';

function CollectionsManagement() {
    const [collections, setCollections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [newCollectionName, setNewCollectionName] = useState('');
    const [creating, setCreating] = useState(false);
    const [generatingSummaries, setGeneratingSummaries] = useState({}); // collectionName -> jobId
    const [expandedSummaries, setExpandedSummaries] = useState({}); // collectionName -> boolean
    const girderUpdatesContext = useGirderUpdates();
    const jobMonitorCleanupRefs = useRef({}); // collectionName -> cleanup function

    useEffect(() => {
        fetchCollections();
    }, []);

    // Cleanup job monitors on unmount
    useEffect(() => {
        return () => {
            Object.values(jobMonitorCleanupRefs.current).forEach(cleanup => {
                if (cleanup) cleanup();
            });
            jobMonitorCleanupRefs.current = {};
        };
    }, []);

    const fetchCollections = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await api.get('agent/rag/collections');
            if (response.status !== 200) {
                throw new Error(`Failed to fetch collections: ${response.status}`);
            }
            setCollections(response.data.collections || []);
        } catch (err) {
            console.error('Error fetching collections:', err);
            setError('Failed to load collections: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateCollection = async (e) => {
        e.preventDefault();
        if (!newCollectionName.trim()) {
            setError('Collection name is required');
            return;
        }

        try {
            setCreating(true);
            setError('');
            const formData = new FormData();
            formData.append('collectionName', newCollectionName.trim());

            const response = await api.post('agent/rag/collections', { body: formData });

            if (response.status !== 200) {
                let errorMessage = `HTTP error! status: ${response.status}`;
                if (response.data) {
                    if (typeof response.data === 'string') {
                        errorMessage = response.data;
                    } else if (response.data.message) {
                        errorMessage = response.data.message;
                    } else if (response.data.error) {
                        errorMessage = response.data.error;
                    }
                }
                throw new Error(errorMessage);
            }

            await fetchCollections();
            setShowCreateForm(false);
            setNewCollectionName('');
        } catch (err) {
            console.error('Error creating collection:', err);
            setError('Failed to create collection: ' + err.message);
        } finally {
            setCreating(false);
        }
    };

    const handleGenerateSummary = async (collectionName, mode = 'full') => {
        try {
            setError('');
            
            const response = await api.post(`agent/rag/collections/${collectionName}/summary?mode=${mode}`);
            
            if (response.status !== 200) {
                let errorMessage = `HTTP error! status: ${response.status}`;
                if (response.data) {
                    if (typeof response.data === 'string') {
                        errorMessage = response.data;
                    } else if (response.data.message) {
                        errorMessage = response.data.message;
                    } else if (response.data.error) {
                        errorMessage = response.data.error;
                    }
                }
                throw new Error(errorMessage);
            }
            
            const jobId = response.data.jobId;
            setGeneratingSummaries(prev => ({ ...prev, [collectionName]: jobId }));
            
            // Clean up any existing monitor for this collection
            if (jobMonitorCleanupRefs.current[collectionName]) {
                jobMonitorCleanupRefs.current[collectionName]();
            }
            
            // Start monitoring job status
            jobMonitorCleanupRefs.current[collectionName] = monitorRagJobStatus(
                jobId,
                {
                    onProgress: () => {}, // Progress handled by job status
                    onSuccess: (result) => {
                        // Summary jobs don't return chunks, so result may be empty or contain summary info
                        setGeneratingSummaries(prev => {
                            const newState = { ...prev };
                            delete newState[collectionName];
                            return newState;
                        });
                        // Refresh collections to show new summary
                        fetchCollections();
                    },
                    onError: (errorMsg) => {
                        setError(`Summary generation failed for "${collectionName}": ${errorMsg}`);
                        setGeneratingSummaries(prev => {
                            const newState = { ...prev };
                            delete newState[collectionName];
                            return newState;
                        });
                    },
                    onComplete: () => {
                        if (jobMonitorCleanupRefs.current[collectionName]) {
                            jobMonitorCleanupRefs.current[collectionName]();
                            delete jobMonitorCleanupRefs.current[collectionName];
                        }
                    },
                },
                {
                    girderUpdatesContext,
                    jobStatusEndpoint: 'agent/rag/jobs',
                    useWebSocket: true,
                }
            );
        } catch (err) {
            console.error('Error generating summary:', err);
            setError('Failed to start summary generation: ' + err.message);
            setGeneratingSummaries(prev => {
                const newState = { ...prev };
                delete newState[collectionName];
                return newState;
            });
        }
    };

    const handleDeleteCollection = async (collectionName) => {
        const collection = collections.find(c => c.collectionName === collectionName);
        const agentCount = collection?.agents?.length || 0;
        const documentCount = collection?.documentCount || 0;

        let confirmMessage = `Are you sure you want to delete the collection "${collectionName}"?\n\n`;
        if (agentCount > 0) {
            confirmMessage += `WARNING: This collection is used by ${agentCount} agent(s): ${collection.agents.map(a => a.name).join(', ')}\n`;
            confirmMessage += `You must remove this collection from all agents first.\n\n`;
        }
        if (documentCount > 0) {
            confirmMessage += `This will delete ${documentCount} document(s) and all their chunks.\n\n`;
        }
        confirmMessage += 'This action cannot be undone.';

        if (!window.confirm(confirmMessage)) {
            return;
        }

        try {
            setError('');
            const response = await api.delete(`agent/rag/collections/${collectionName}`);

            if (response.status !== 200) {
                const errorData = typeof response.data === 'string' ? {} : response.data;
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }

            await fetchCollections();
        } catch (err) {
            console.error('Error deleting collection:', err);
            setError('Failed to delete collection: ' + err.message);
        }
    };

    if (loading) {
        return (
            <SetupPortalPage>
                <div>Loading collections...</div>
            </SetupPortalPage>
        );
    }

    return (
        <SetupPortalPage>
            <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
                <div style={{ marginBottom: '20px' }}>
                    <BackToAgentSetupLink />
                </div>
                
                <h1>Collections Management</h1>
                
                {error && (
                    <div style={{ color: 'red', marginBottom: '20px', padding: '10px', backgroundColor: '#ffe6e6', borderRadius: '5px' }}>
                        {error}
                    </div>
                )}

                <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
                    <button
                        onClick={() => {
                            setShowCreateForm(!showCreateForm);
                            setNewCollectionName('');
                            setError('');
                        }}
                        style={{
                            padding: '10px 20px',
                            fontSize: '16px',
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: 'pointer',
                        }}
                    >
                        {showCreateForm ? 'Cancel' : 'Create New Collection'}
                    </button>
                    <button
                        onClick={() => {
                            setError('');
                            fetchCollections();
                        }}
                        disabled={loading}
                        style={{
                            padding: '10px 20px',
                            fontSize: '16px',
                            backgroundColor: loading ? '#ccc' : '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: loading ? 'not-allowed' : 'pointer',
                        }}
                    >
                        {loading ? 'Refreshing...' : 'Refresh Collections'}
                    </button>
                </div>

                {showCreateForm && (
                    <div style={{ marginBottom: '30px', padding: '20px', backgroundColor: '#f9f9f9', borderRadius: '5px' }}>
                        <h2>Create New Collection</h2>
                        <form onSubmit={handleCreateCollection}>
                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                    Collection Name: *
                                </label>
                                <input
                                    type="text"
                                    value={newCollectionName}
                                    onChange={(e) => setNewCollectionName(e.target.value)}
                                    required
                                    placeholder="Enter collection name"
                                    style={{ width: '100%', maxWidth: '400px', padding: '8px', fontSize: '14px' }}
                                />
                                <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                                    Collections can be shared across multiple agents
                                </div>
                            </div>

                            <div>
                                <button
                                    type="submit"
                                    disabled={creating}
                                    style={{
                                        padding: '10px 20px',
                                        fontSize: '16px',
                                        backgroundColor: '#28a745',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '5px',
                                        cursor: creating ? 'not-allowed' : 'pointer',
                                    }}
                                >
                                    {creating ? 'Creating...' : 'Create Collection'}
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                <div>
                    <h2>Collections ({collections.length})</h2>
                    {collections.length === 0 ? (
                        <div>No collections found. Create one to get started.</div>
                    ) : (
                        <div style={{ display: 'grid', gap: '15px' }}>
                            {collections.map(collection => (
                                <div
                                    key={collection.collectionName}
                                    style={{
                                        padding: '20px',
                                        backgroundColor: '#fff',
                                        border: '1px solid #ddd',
                                        borderRadius: '5px',
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '10px' }}>
                                        <div>
                                            <h3 style={{ margin: '0 0 5px 0' }}>{collection.collectionName}</h3>
                                        </div>
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <Link
                                                to={`${AGENT_SETUP_BASE}/documents?collection=${encodeURIComponent(collection.collectionName)}`}
                                                style={{
                                                    padding: '5px 15px',
                                                    fontSize: '14px',
                                                    backgroundColor: '#007bff',
                                                    color: 'white',
                                                    textDecoration: 'none',
                                                    borderRadius: '3px',
                                                    display: 'inline-block',
                                                }}
                                            >
                                                View Documents
                                            </Link>
                                            <button
                                                onClick={() => handleDeleteCollection(collection.collectionName)}
                                                style={{
                                                    padding: '5px 15px',
                                                    fontSize: '14px',
                                                    backgroundColor: '#dc3545',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '3px',
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                    <div style={{ marginTop: '15px', fontSize: '14px' }}>
                                        <div><strong>Documents:</strong> {collection.documentCount || 0}</div>
                                        <div style={{ marginTop: '5px' }}><strong>Chunks:</strong> {collection.chunkCount || 0}</div>
                                        
                                        {/* Summary Section */}
                                        <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                                <strong>Summary:</strong>
                                                <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                                                    {!generatingSummaries[collection.collectionName] && (
                                                        <select
                                                            id={`summary-mode-${collection.collectionName}`}
                                                            defaultValue="full"
                                                            style={{
                                                                padding: '3px 6px',
                                                                fontSize: '12px',
                                                                borderRadius: '3px',
                                                                border: '1px solid #ccc',
                                                            }}
                                                        >
                                                            <option value="full">Full Regeneration</option>
                                                            <option value="incremental">Incremental Update</option>
                                                        </select>
                                                    )}
                                                    <button
                                                        onClick={() => {
                                                            const modeSelect = document.getElementById(`summary-mode-${collection.collectionName}`);
                                                            const mode = modeSelect ? modeSelect.value : 'full';
                                                            handleGenerateSummary(collection.collectionName, mode);
                                                        }}
                                                        disabled={!!generatingSummaries[collection.collectionName]}
                                                        style={{
                                                            padding: '3px 8px',
                                                            fontSize: '12px',
                                                            backgroundColor: generatingSummaries[collection.collectionName] ? '#6c757d' : '#28a745',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '3px',
                                                            cursor: generatingSummaries[collection.collectionName] ? 'not-allowed' : 'pointer',
                                                        }}
                                                    >
                                                        {generatingSummaries[collection.collectionName] ? 'Generating...' : 'Generate'}
                                                    </button>
                                                    {collection.summary && (
                                                        <button
                                                            onClick={() => setExpandedSummaries(prev => ({
                                                                ...prev,
                                                                [collection.collectionName]: !prev[collection.collectionName]
                                                            }))}
                                                            style={{
                                                                padding: '3px 8px',
                                                                fontSize: '12px',
                                                                backgroundColor: '#007bff',
                                                                color: 'white',
                                                                border: 'none',
                                                                borderRadius: '3px',
                                                                cursor: 'pointer',
                                                            }}
                                                        >
                                                            {expandedSummaries[collection.collectionName] ? 'Hide' : 'Show'}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            {generatingSummaries[collection.collectionName] && (
                                                <div style={{ fontSize: '12px', color: '#666', fontStyle: 'italic', marginBottom: '5px' }}>
                                                    Generating summary... (Job ID: {generatingSummaries[collection.collectionName]})
                                                </div>
                                            )}
                                            {collection.summary && expandedSummaries[collection.collectionName] && (
                                                <div style={{ 
                                                    marginTop: '10px', 
                                                    padding: '10px', 
                                                    backgroundColor: 'white', 
                                                    borderRadius: '3px',
                                                    whiteSpace: 'pre-wrap',
                                                    fontSize: '13px',
                                                    lineHeight: '1.5'
                                                }}>
                                                    {collection.summary}
                                                </div>
                                            )}
                                            {collection.summaryGeneratedAt && (
                                                <div style={{ fontSize: '11px', color: '#666', marginTop: '5px' }}>
                                                    Generated: {new Date(collection.summaryGeneratedAt).toLocaleString()}
                                                    {collection.summaryMode && ` (${collection.summaryMode})`}
                                                </div>
                                            )}
                                            {!collection.summary && !generatingSummaries[collection.collectionName] && (
                                                <div style={{ fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
                                                    No summary available. Click "Generate" to create one.
                                                </div>
                                            )}
                                        </div>
                                        
                                        {collection.agents && collection.agents.length > 0 && (
                                            <div style={{ marginTop: '10px' }}>
                                                <strong>Used by agents:</strong>
                                                <div style={{ marginTop: '5px', display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                                                    {collection.agents.map(agent => (
                                                        <Link
                                                            key={agent.agentId}
                                                            to={`${AGENT_SETUP_BASE}/manage`}
                                                            style={{
                                                                padding: '3px 8px',
                                                                backgroundColor: '#e9ecef',
                                                                color: '#007bff',
                                                                textDecoration: 'none',
                                                                borderRadius: '10px',
                                                                fontSize: '12px',
                                                            }}
                                                        >
                                                            {agent.name}
                                                        </Link>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </SetupPortalPage>
    );
}

export default CollectionsManagement;
